import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../css/admin-tokens.css';
import '../css/pirogov-dev.css';
import exitIcon from '../img/agent/exit.svg';
import pauseIcon from '../img/agent/pause.svg';
import playIcon from '../img/agent/play.svg';
import reloadIcon from '../img/agent/reload.svg';
import chatIcon from '../img/agent/chat.svg';
import fileIcon from '../img/agent/file.svg';
import terminalIcon from '../img/agent/terminal.svg';
import editIcon from '../img/agent/edit.svg';
import timeIcon from '../img/agent/time.svg';
import maleIcon from '../img/agent/male.svg';
import femaleIcon from '../img/agent/female.svg';
import plusIcon from '../img/agent/plus.svg';
import historyIcon from '../img/agent/history.svg';
import trashIcon from '../img/agent/trash.svg';
import downloadIcon from '../img/agent/download.svg';

// ════════════════════════════════════════════════════════
//  Admin Token Manager — React SPA component
// ════════════════════════════════════════════════════════

const API_BASE = '/api/admin';

function getCsrfToken() {
  if (!document.cookie) return null;
  const match = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith('csrftoken='));
  return match ? decodeURIComponent(match.substring('csrftoken='.length)) : null;
}

function api(endpoint, options = {}) {
  const pw = sessionStorage.getItem('admin_password') || '';
  const headers = {
    'Content-Type': 'application/json',
    'X-Admin-Password': pw,
    'X-CSRFToken': getCsrfToken() || '',
    ...options.headers,
  };
  return fetch(`${API_BASE}${endpoint}`, {
    credentials: 'include',
    ...options,
    headers,
  }).then(async (res) => {
    const data = await res.json();
    if (!res.ok) throw { status: res.status, ...data };
    return data;
  });
}

function apiBlob(endpoint) {
  const pw = sessionStorage.getItem('admin_password') || '';
  return fetch(`${API_BASE}${endpoint}`, {
    credentials: 'include',
    headers: { 'X-Admin-Password': pw, 'X-CSRFToken': getCsrfToken() || '' },
  });
}

// ── Date formatting ──
function fmtDate(v) {
  if (!v) return '—';
  try {
    const d = typeof v === 'number' ? new Date(v < 1e12 ? v * 1000 : v) : new Date(v);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return String(v).slice(0, 10);
  }
}
function daysLeft(expiresAt) {
  if (!expiresAt) return null;
  const diff = (new Date(expiresAt) - new Date()) / 86_400_000;
  return Math.ceil(diff);
}
function fmtSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

// ── Audio helpers ──
function base64AudioToUrl(base64) {
  try {
    const byteStr = atob(base64);
    const len = byteStr.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = byteStr.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'audio/webm' });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

// ════════════════════════════════════════════════════════
//  Icon component
// ════════════════════════════════════════════════════════
const Icon = ({ src, alt = '', size = '1.25em', className = '', style = {} }) => (
  <img src={src} alt={alt} className={className} style={{ display: 'inline', width: size, height: size, verticalAlign: 'middle', filter: 'brightness(0) invert(1)', ...style }} />
);

// ════════════════════════════════════════════════════════
//  Login screen
// ════════════════════════════════════════════════════════

const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [isSetup, setIsSetup] = useState(false);
  const [confirmPw, setConfirmPw] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/password-exists/`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { setIsSetup(!d.password_exists); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (isSetup) {
      if (password !== confirmPw) { setError('Пароли не совпадают'); return; }
      if (password.length < 4) { setError('Минимум 4 символа'); return; }
      try {
        await fetch(`${API_BASE}/setup-password/`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-CSRFToken': getCsrfToken() || '' },
          body: JSON.stringify({ password }),
        }).then((r) => r.json());
        sessionStorage.setItem('admin_password', password);
        onLogin();
      } catch (err) {
        setError(err.message || 'Ошибка при установке пароля');
      }
    } else {
      try {
        sessionStorage.setItem('admin_password', password);
        await api('/verify-password/', { method: 'POST', body: '{}' });
        onLogin();
      } catch (err) {
        sessionStorage.removeItem('admin_password');
        setError(err.error === 'unauthorized' ? 'Неверный пароль' : (err.message || 'Ошибка'));
      }
    }
  };

  if (loading) return <div className="admin-root"><div className="admin-spinner" /></div>;

  return (
    <div className="admin-root">
      <div className="admin-login">
        <form className="admin-login__box" onSubmit={handleSubmit}>
          <div className="admin-login__title">🔐 {isSetup ? 'Создание пароля' : 'Вход в панель'}</div>
          <div className="admin-login__subtitle">
            {isSetup ? 'Задайте пароль администратора для первого входа' : 'Управление API токенами'}
          </div>
          <input
            type="password"
            className="admin-login__input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={isSetup ? 'Новый пароль' : 'Пароль администратора'}
            autoFocus
          />
          {isSetup && (
            <input
              type="password"
              className="admin-login__input"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              placeholder="Подтвердите пароль"
            />
          )}
          <button type="submit" className="admin-login__btn" disabled={!password}>
            {isSetup ? 'Создать пароль' : 'Войти'}
          </button>
          {error && <div className="admin-login__error">{error}</div>}
        </form>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  Create token modal
// ════════════════════════════════════════════════════════

const CreateTokenModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [daysValid, setDaysValid] = useState('365');
  const [maxReq, setMaxReq] = useState('500');
  const [resetRequests, setResetRequests] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await api('/tokens/create/', {
        method: 'POST',
        body: JSON.stringify({ name, days_valid: parseInt(daysValid), max_requests_per_month: parseInt(maxReq), reset_requests: resetRequests }),
      });
      setResult(data.token);
      onCreated();
    } catch (err) {
      setError(err.error || 'Ошибка создания токена');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <span className="admin-modal__title"><Icon src={plusIcon} /> Новый API токен</span>
          <button className="admin-modal__close" onClick={onClose}>✕</button>
        </div>
        <div className="admin-modal__body">
          {!result ? (
            <form onSubmit={handleCreate}>
              <div className="admin-form-group">
                <label className="admin-form-group__label">Имя сервиса / клиента</label>
                <input className="admin-form-group__input" value={name} onChange={(e) => setName(e.target.value)} placeholder="MyService" required />
              </div>
              <div className="admin-form-group">
                <label className="admin-form-group__label">Срок действия (дней)</label>
                <input className="admin-form-group__input" type="number" min="1" value={daysValid} onChange={(e) => setDaysValid(e.target.value)} />
              </div>
              <div className="admin-form-group">
                <label className="admin-form-group__label">Лимит запросов / месяц</label>
                <input className="admin-form-group__input" type="number" min="1" value={maxReq} onChange={(e) => setMaxReq(e.target.value)} />
              </div>
              <label className="admin-toggle-row">
                <input type="checkbox" checked={resetRequests} onChange={(e) => setResetRequests(e.target.checked)} />
                <span>Сбрасывать счётчик каждые 30 дней</span>
              </label>
              <div className="admin-form-group__hint" style={{ marginTop: -8, marginBottom: 12 }}>
                {resetRequests ? 'Счётчик обнуляется каждые 30 дней от даты создания' : 'Лимит на весь срок действия, не сбрасывается'}
              </div>
              {error && <div className="admin-error">{error}</div>}
              <div className="admin-form-actions">
                <button type="button" className="admin-btn admin-btn--ghost" onClick={onClose}>Отмена</button>
                <button type="submit" className="admin-btn admin-btn--primary" disabled={loading || !name}>{loading ? 'Создание...' : 'Создать'}</button>
              </div>
            </form>
          ) : (
            <div className="admin-token-result">
              <div className="admin-token-result__title">✅ Токен создан</div>
              <div className="admin-token-result__row"><span className="admin-token-result__label">Имя:</span><span className="admin-token-result__value">{result.name}</span></div>
              <div className="admin-token-result__row"><span className="admin-token-result__label">Токен:</span><span className="admin-token-result__value">{result.token}</span></div>
              <div className="admin-token-result__row"><span className="admin-token-result__label">Секрет:</span><span className="admin-token-result__value">{result.secret}</span></div>
              <div className="admin-token-result__row"><span className="admin-token-result__label">Истекает:</span><span className="admin-token-result__value">{fmtDate(result.expires_at)}</span></div>
              <div className="admin-token-result__row"><span className="admin-token-result__label">Лимит/мес:</span><span className="admin-token-result__value">{result.max_requests_per_month}</span></div>
              <div className="admin-token-result__row"><span className="admin-token-result__label">Сброс:</span><span className="admin-token-result__value">{resetRequests ? 'Каждые 30 дней' : 'Нет (общий лимит)'}</span></div>
              <div className="admin-token-result__warning">⚠️ Сохраните токен и секрет прямо сейчас — они больше не будут показаны!</div>
              <div className="admin-form-actions" style={{ marginTop: 16 }}>
                <button className="admin-btn admin-btn--primary" onClick={onClose}>Закрыть</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  Renew token modal
// ════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════
//  Edit token modal (rename, change limits, delete)
// ════════════════════════════════════════════════════════

const EditTokenModal = ({ token, onClose, onDone }) => {
  const [name, setName] = useState(token.name || '');
  const [maxReq, setMaxReq] = useState(String(token.max_requests_per_month || 500));
  const [resetRequests, setResetRequests] = useState(token.reset_requests !== false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      const fields = { token_hash: token.token_hash };
      if (name !== token.name) fields.name = name;
      if (Number(maxReq) !== token.max_requests_per_month) fields.max_requests_per_month = Number(maxReq);
      if (resetRequests !== (token.reset_requests !== false)) fields.reset_requests = resetRequests;
      if (!fields.name && fields.max_requests_per_month === undefined && fields.reset_requests === undefined) { onClose(); return; }
      await api('/tokens/update/', { method: 'POST', body: JSON.stringify(fields) });
      onDone();
      onClose();
    } catch (e) {
      setError(e.error || 'Ошибка обновления');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Удалить токен «${token.name}»? Это действие необратимо.`)) return;
    setDeleting(true);
    setError(null);
    try {
      await api('/tokens/delete/', { method: 'POST', body: JSON.stringify({ token_hash: token.token_hash }) });
      onDone();
      onClose();
    } catch (e) {
      setError(e.error || 'Ошибка удаления');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <span className="admin-modal__title">Редактирование — {token.name}</span>
          <button className="admin-modal__close" onClick={onClose}>✕</button>
        </div>
        <div className="admin-modal__body">
          <div className="admin-form-group">
            <label className="admin-form-group__label">Имя сервиса / клиента</label>
            <input className="admin-form-group__input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="admin-form-group">
            <label className="admin-form-group__label">Лимит запросов</label>
            <input className="admin-form-group__input" type="number" min="1" value={maxReq} onChange={(e) => setMaxReq(e.target.value)} />
            <div className="admin-form-group__hint">Текущее использование: {token.current_month_requests} / {token.max_requests_per_month}</div>
          </div>
          <label className="admin-toggle-row">
            <input type="checkbox" checked={resetRequests} onChange={(e) => setResetRequests(e.target.checked)} />
            <span>Сбрасывать счётчик каждые 30 дней</span>
          </label>
          <div className="admin-form-group__hint" style={{ marginTop: -4, marginBottom: 12 }}>
            {resetRequests ? 'Счётчик обнуляется каждые 30 дней от даты создания' : 'Лимит на весь срок действия, не сбрасывается'}
          </div>
          {error && <div className="admin-error">{error}</div>}
          <div className="admin-form-actions">
            <button className="admin-btn admin-btn--danger" onClick={handleDelete} disabled={deleting || loading}>
              {deleting ? '...' : <><Icon src={trashIcon} /> Удалить</>}
            </button>
            <span style={{ flex: 1 }} />
            <button className="admin-btn admin-btn--ghost" onClick={onClose}>Отмена</button>
            <button className="admin-btn admin-btn--primary" onClick={handleSave} disabled={loading || deleting}>
              {loading ? '...' : 'Сохранить'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RenewModal = ({ tokenHash, tokenName, onClose, onDone }) => {
  const [days, setDays] = useState('90');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleRenew = async () => {
    setError(null);
    setLoading(true);
    try {
      await api('/tokens/renew/', { method: 'POST', body: JSON.stringify({ token_hash: tokenHash, additional_days: parseInt(days) }) });
      onDone();
      onClose();
    } catch (err) {
      setError(err.error || 'Ошибка продления');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <span className="admin-modal__title"><Icon src={timeIcon} /> Продлить «{tokenName}»</span>
          <button className="admin-modal__close" onClick={onClose}>✕</button>
        </div>
        <div className="admin-modal__body">
          <div className="admin-form-group">
            <label className="admin-form-group__label">Дополнительных дней (от сегодня)</label>
            <input className="admin-form-group__input" type="number" min="1" value={days} onChange={(e) => setDays(e.target.value)} />
          </div>
          {error && <div className="admin-error">{error}</div>}
          <div className="admin-form-actions">
            <button className="admin-btn admin-btn--ghost" onClick={onClose}>Отмена</button>
            <button className="admin-btn admin-btn--primary" disabled={loading} onClick={handleRenew}>{loading ? '...' : 'Продлить'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  Chat messages rendered with pd-* styles (like user UI)
// ════════════════════════════════════════════════════════

const ChatMessageView = ({ messages, onDownload }) => {
  if (!messages || messages.length === 0) {
    return <div className="admin-history__empty">Нет сообщений</div>;
  }

  return (
    <div className="admin-chat-view pd-messages" style={{ padding: '16px 20px', gap: 18 }}>
      {messages.map((msg, i) => {
        const key = `msg-${i}`;

        // User query (text or audio)
        if (msg.role === 'user' || msg.type === 'query' || msg.type === 'clarification_answer' || msg.type === 'scene_edit_query') {
          const text = msg.query || msg.text || '';
          const hasAudio = msg.audio && typeof msg.audio === 'string' && msg.audio.length > 100;
          const transcription = msg.transcription || '';
          if (!text && !hasAudio) return null;
          return (
            <div key={key} className="pd-msg-right" style={{ animation: 'none' }}>
              {hasAudio && (
                <AudioPlayer base64={msg.audio} />
              )}
              {transcription && !text && (
                <div className="pd-user-bubble">
                  <span className="admin-chat-transcription-label">🎙 Расшифровка:</span> {transcription}
                </div>
              )}
              {text && <div className="pd-user-bubble">{text}</div>}
            </div>
          );
        }

        // Off-topic response
        if (msg.type === 'off_topic') {
          return (
            <div key={key} className="pd-msg-left" style={{ animation: 'none' }}>
              <div className="pd-ai-bubble">{msg.response_text || ''}</div>
            </div>
          );
        }

        // Clarification
        if (msg.type === 'clarification') {
          return (
            <div key={key} className="pd-msg-left" style={{ animation: 'none' }}>
              <div className="pd-ai-bubble">{msg.text || msg.response_text || ''}</div>
            </div>
          );
        }

        // Generation (scene created)
        if (msg.type === 'generation') {
          return (
            <div key={key} className="pd-scene-response" style={{ animation: 'none' }}>
              {/* AI thoughts */}
              {msg.saved_thoughts && msg.saved_thoughts.length > 0 && msg.saved_thoughts.map((t, ti) => (
                <div key={`th-${ti}`} className="pd-ai-thought" style={{ animation: 'none' }}>
                  <span className="pd-ai-thought__label">{t.label || '🧠 Мысли ИИ:'}</span>
                  {t.text}
                </div>
              ))}

              {/* Response text */}
              {msg.response_text && (
                <div className="pd-msg-left" style={{ animation: 'none' }}>
                  <div className="pd-ai-bubble">{msg.response_text}</div>
                </div>
              )}

              {/* Planning details */}
              {msg.scene_planning_info && (
                <PlanningBlock info={msg.scene_planning_info} details={msg.saved_planning_details} />
              )}

              {/* Download button */}
              {msg.download_url && (
                <button
                  className="pd-download-btn"
                  style={{ animation: 'none' }}
                  onClick={() => onDownload(msg.download_url.split('/').pop())}
                >Скачать сцену</button>
              )}
            </div>
          );
        }

        // Scene edit
        if (msg.type === 'scene_edit') {
          return (
            <div key={key} className="pd-scene-response" style={{ animation: 'none' }}>
              {msg.response_text && (
                <div className="pd-msg-left" style={{ animation: 'none' }}>
                  <div className="pd-ai-bubble">{msg.response_text}</div>
                </div>
              )}

              {/* Completion report */}
              {msg.completion_report && (
                <div className="pd-completion-report" style={{ animation: 'none' }}>
                  {msg.completion_report.done && msg.completion_report.done.map((item, di) => (
                    <div key={`d-${di}`} className="pd-report-done">✅ {item}</div>
                  ))}
                  {msg.completion_report.failed && msg.completion_report.failed.map((item, fi) => (
                    <div key={`f-${fi}`} className="pd-report-failed">❌ {item}</div>
                  ))}
                </div>
              )}

              {msg.download_url && (
                <button
                  className="pd-download-btn"
                  style={{ animation: 'none' }}
                  onClick={() => onDownload(msg.download_url.split('/').pop())}
                >Скачать сцену</button>
              )}
            </div>
          );
        }

        // Fallback — generic text
        return (
          <div key={key} className="pd-msg-left" style={{ animation: 'none' }}>
            <div className="pd-ai-bubble">{msg.response_text || msg.text || JSON.stringify(msg)}</div>
          </div>
        );
      })}
    </div>
  );
};

const PlanningBlock = ({ info, details }) => {
  const [open, setOpen] = useState(false);
  if (!info && (!details || details.length === 0)) return null;

  return (
    <div className="pd-planning" style={{ animation: 'none' }}>
      <div className="pd-planning__header" onClick={() => setOpen(!open)}>
        <span>📋 Планирование сцены</span>
        <span className={`pd-planning__chevron${open ? ' pd-planning__chevron--open' : ''}`}>▼</span>
      </div>
      {open && (
        <div className="pd-planning__body">
          {info && (
            <>
              {info.visualization_intent && <div className="pd-planning__section"><div className="pd-planning__label">Визуализация:</div>{info.visualization_intent}</div>}
              {info.focus_choice && <div className="pd-planning__section"><div className="pd-planning__label">Фокус:</div>{info.focus_choice}</div>}
              {info.context_choice && <div className="pd-planning__section"><div className="pd-planning__label">Контекст:</div>{info.context_choice}</div>}
              {info.view_recommendation && <div className="pd-planning__section"><div className="pd-planning__label">Вид:</div>{info.view_recommendation}</div>}
              {info.rationale && <div className="pd-planning__section"><div className="pd-planning__label">Обоснование:</div>{info.rationale}</div>}
            </>
          )}
          {details && details.map((d, i) => (
            <div key={i} className="pd-planning__section">
              <div className="pd-planning__label">{d.label}:</div>
              {d.text}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  Audio player for base64 audio in chat messages
// ════════════════════════════════════════════════════════

const AudioPlayer = ({ base64 }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!base64) return;
    const blobUrl = base64AudioToUrl(base64);
    setUrl(blobUrl);
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [base64]);
  if (!url) return null;
  return (
    <div className="admin-audio-player">
      <audio controls preload="metadata" src={url} style={{ width: '100%', maxWidth: 300, height: 36 }} />
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  Chat list & view modals for admin chat browsing
// ════════════════════════════════════════════════════════

const BODIES = [
  { value: 'structures.json', label: 'Анатомия', icon: maleIcon },
];

const ChatListModal = ({ tokenHash, tokenName, onClose }) => {
  const [body, setBody] = useState('structures.json');
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewChat, setViewChat] = useState(null);

  // ── Search state ──
  const [searchChatText, setSearchChatText] = useState('');
  const [searchChatLoading, setSearchChatLoading] = useState(false);
  const [searchChatResults, setSearchChatResults] = useState([]);

  const [searchJsonText, setSearchJsonText] = useState('');
  const [searchJsonLoading, setSearchJsonLoading] = useState(false);
  const [searchJsonResults, setSearchJsonResults] = useState([]);

  const [searchPresetName, setSearchPresetName] = useState('');
  const [searchPresetLoading, setSearchPresetLoading] = useState(false);
  const [searchPresetResults, setSearchPresetResults] = useState([]);

  // ── Debounce timers ──
  const chatSearchTimerRef = useRef(null);
  const jsonSearchTimerRef = useRef(null);
  const presetSearchTimerRef = useRef(null);

  const handleSearchChatTextChange = (e) => {
    const value = e.target.value;
    setSearchChatText(value);
    if (chatSearchTimerRef.current) clearTimeout(chatSearchTimerRef.current);
    if (!value.trim()) { setSearchChatResults([]); return; }
    chatSearchTimerRef.current = setTimeout(async () => {
      setSearchChatLoading(true);
      try {
        const data = await api(`/search/chat-text/?q=${encodeURIComponent(value)}`);
        setSearchChatResults(data.results || []);
      } catch (err) {
        console.error('Search chat-text error:', err);
      } finally {
        setSearchChatLoading(false);
      }
    }, 400);
  };

  const handleSearchJsonTextChange = (e) => {
    const value = e.target.value;
    setSearchJsonText(value);
    if (jsonSearchTimerRef.current) clearTimeout(jsonSearchTimerRef.current);
    if (!value.trim()) { setSearchJsonResults([]); return; }
    jsonSearchTimerRef.current = setTimeout(async () => {
      setSearchJsonLoading(true);
      try {
        const data = await api(`/search/json-text/?q=${encodeURIComponent(value)}`);
        setSearchJsonResults(data.results || []);
      } catch (err) {
        console.error('Search json-text error:', err);
      } finally {
        setSearchJsonLoading(false);
      }
    }, 400);
  };

  const handleSearchPresetNameChange = (e) => {
    const value = e.target.value;
    setSearchPresetName(value);
    if (presetSearchTimerRef.current) clearTimeout(presetSearchTimerRef.current);
    if (!value.trim()) { setSearchPresetResults([]); return; }
    presetSearchTimerRef.current = setTimeout(async () => {
      setSearchPresetLoading(true);
      try {
        const data = await api(`/search/preset-name/?q=${encodeURIComponent(value)}`);
        setSearchPresetResults(data.results || []);
      } catch (err) {
        console.error('Search preset-name error:', err);
      } finally {
        setSearchPresetLoading(false);
      }
    }, 400);
  };

  const handleDownloadPreset = async (filename) => {
    try {
      const res = await apiBlob(`/download-preset/?file=${encodeURIComponent(filename)}`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  const loadChats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api(`/chats/?body=${encodeURIComponent(body)}`);
      const tokenData = (data.tokens || {})[tokenHash.slice(0, 16)];
      setChats(tokenData ? tokenData.chats : []);
    } catch {
      setChats([]);
    } finally {
      setLoading(false);
    }
  }, [body, tokenHash]);

  useEffect(() => { loadChats(); }, [loadChats]);

  // ── Local filtered list ──
  const filteredChats = chats.filter((chat) => {
    if (!searchChatText.trim()) return true;
    const q = searchChatText.toLowerCase();
    return (
      (chat.title || '').toLowerCase().includes(q) ||
      (chat.first_message || '').toLowerCase().includes(q) ||
      (chat.created_at || '').includes(q)
    );
  });

  if (viewChat) {
    return (
      <ChatViewModal
        chatId={viewChat.chat_id}
        chatTitle={viewChat.title}
        body={body}
        tokenHash={viewChat._token_hash || tokenHash}
        onBack={() => setViewChat(null)}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-modal admin-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <span className="admin-modal__title"><Icon src={chatIcon} /> Переписки — {tokenName || tokenHash.slice(0, 12)}</span>
          <button className="admin-modal__close" onClick={onClose}>✕</button>
        </div>
        <div className="admin-modal__body">

        {/* Search section */}
        <div className="admin-search-section">
            <div className="admin-search-section__row">
              <input
                type="text"
                className="admin-search-block__input"
                placeholder="Текст в сообщениях..."
                value={searchChatText}
                onChange={handleSearchChatTextChange}
              />
              <input
                type="text"
                className="admin-search-block__input"
                placeholder="Текст в JSON..."
                value={searchJsonText}
                onChange={handleSearchJsonTextChange}
              />
              <input
                type="text"
                className="admin-search-block__input"
                placeholder="Имя файла пресета..."
                value={searchPresetName}
                onChange={handleSearchPresetNameChange}
              />
            </div>

            {/* Chat text results */}
            {searchChatResults.length > 0 && (
              <div className="admin-search-results">
                {searchChatResults.map((r) => (
                  <div key={r.chat_id} className="admin-search-result-item" onClick={() => setViewChat({ chat_id: r.chat_id, title: r.title, _token_hash: r.token_hash })} style={{ cursor: 'pointer' }}>
                    <div className="admin-search-result-item__title">{r.title || r.chat_id}</div>
                    <div className="admin-search-result-item__meta">
                      {r.token_hash} · {r.matches?.length || 0} совпадений
                    </div>
                    {r.matches && r.matches.slice(0, 2).map((m, mi) => (
                      <div key={mi} className="admin-search-result-item__snippet">…{m.snippet}…</div>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {!searchChatLoading && searchChatResults.length === 0 && searchChatText && (
              <div className="admin-history__empty" style={{ padding: 8 }}>Ничего не найдено</div>
            )}

            {/* JSON text results */}
            {searchJsonResults.length > 0 && (
              <div className="admin-search-results">
                {searchJsonResults.map((r, idx) => (
                    <div key={idx} className="admin-search-result-item" onClick={() => handleDownloadPreset(r.filename)} style={{ cursor: 'pointer' }}>
                      <div className="admin-search-result-item__title">📄 {r.filename}</div>
                      <div className="admin-search-result-item__meta">
                        {r.path} · {r.snippet}
                      </div>
                    </div>
                ))}
              </div>
            )}
            {!searchJsonLoading && searchJsonResults.length === 0 && searchJsonText && (
              <div className="admin-history__empty" style={{ padding: 8 }}>Ничего не найдено</div>
            )}

            {/* Preset name results */}
            {searchPresetResults.length > 0 && (
              <div className="admin-search-results">
                {searchPresetResults.map((r, idx) => (
                  <div key={idx} className="admin-search-result-item" onClick={() => handleDownloadPreset(r.filename)} style={{ cursor: 'pointer' }}>
                    <div className="admin-search-result-item__title">{r.filename}</div>
                    <div className="admin-search-result-item__meta">
                      {r.path} · {r.size > 1024 ? `${(r.size/1024).toFixed(1)} KB` : `${r.size} B`}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!searchPresetLoading && searchPresetResults.length === 0 && searchPresetName && (
              <div className="admin-history__empty" style={{ padding: 8 }}>Ничего не найдено</div>
            )}
          </div>

          {!searchChatText && !searchJsonText && !searchPresetName && (
            <>
              <div className="admin-token-chats__header" style={{ marginBottom: 12 }}>
                <div className="pd-body-toggle">
                  {BODIES.map((b) => (
                    <button key={b.value} className={`pd-body-toggle__btn${body === b.value ? ' pd-body-toggle__btn--active' : ' pd-body-toggle__btn--inactive'}`} onClick={() => setBody(b.value)}>
                      <img src={b.icon} alt="" className="pd-btn-icon" />
                      <span className="pd-btn-label">{b.label}</span>
                    </button>
                  ))}
                </div>
                <span className="admin-token-chats__count">{filteredChats.length} / {chats.length} переписок</span>
              </div>

              {loading ? <div className="admin-spinner" style={{ padding: 16 }} /> : filteredChats.length === 0 ? (
                <div className="admin-history__empty" style={{ padding: 16 }}>{chats.length === 0 ? 'Нет переписок' : 'Ничего не найдено'}</div>
              ) : (
                <div className="admin-chatlist">
                  {filteredChats.map((chat) => (
                    <div key={chat.chat_id} className="admin-chatlist__item" onClick={() => setViewChat(chat)}>
                      <div className="admin-chatlist__title">
                        {chat.title || 'Без названия'}
                        {chat.mode && <span className="admin-chats__chat-mode">{chat.mode}</span>}
                      </div>
                      <div className="admin-chatlist__meta">
                        <Icon src={chatIcon} /> {chat.message_count} · <Icon src={fileIcon} /> {chat.scene_count}
                        {chat.preset_files && chat.preset_files.length > 0 && ` · ${chat.preset_files.length} пресетов`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ChatViewModal = ({ chatId, chatTitle, body, tokenHash, onBack, onClose }) => {
  const [chatDetail, setChatDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await api(
          `/chats/view/?chat_id=${encodeURIComponent(chatId)}&body=${encodeURIComponent(body)}&token_hash=${encodeURIComponent(tokenHash)}`
        );
        setChatDetail(data.chat);
      } catch {
        setChatDetail(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [chatId, body, tokenHash]);

  const handleDownload = async (filename) => {
    try {
      const res = await apiBlob(`/download-preset/?file=${encodeURIComponent(filename)}`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-modal admin-modal--fullchat" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={onBack}>← Назад</button>
          <span className="admin-modal__title" style={{ flex: 1, textAlign: 'center' }}>
            {chatTitle || 'Переписка'}
          </span>
          <button className="admin-modal__close" onClick={onClose}>✕</button>
        </div>
        <div className="admin-modal__body admin-modal__body--chat">
          {loading ? <div className="admin-spinner" /> : !chatDetail ? (
            <div className="admin-history__empty">Не удалось загрузить</div>
          ) : (
            <>
              {chatDetail.preset_files && chatDetail.preset_files.length > 0 && (
                <div className="admin-chats__presets">
                  <div className="admin-chats__presets-title">Пресеты:</div>
                  {chatDetail.preset_files.map((pf) => (
                    <button key={pf} className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => handleDownload(pf)}><Icon src={downloadIcon} /> {pf}</button>
                  ))}
                </div>
              )}
              <ChatMessageView messages={chatDetail.messages || []} onDownload={handleDownload} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  Terminal output display
// ════════════════════════════════════════════════════════

const TerminalOutput = ({ lines, onClear }) => {
  const termRef = React.useRef(null);
  React.useEffect(() => {
    if (termRef.current) termRef.current.scrollTop = termRef.current.scrollHeight;
  }, [lines]);
  if (!lines || lines.length === 0) return null;
  return (
    <div style={{ position: 'relative' }}>
      {onClear && (
        <button className="admin-terminal__clear" onClick={onClear} title="Очистить">✕</button>
      )}
      <div className="admin-terminal" ref={termRef}>
        {lines.map((line, i) => (
          <div key={i} className={`admin-terminal__line${line.type === 'cmd' ? ' admin-terminal__line--cmd' : line.type === 'error' ? ' admin-terminal__line--error' : line.type === 'success' ? ' admin-terminal__line--success' : ''}`}>
            {line.type === 'cmd' && <span className="admin-terminal__prompt">$ </span>}
            {line.text}
          </div>
        ))}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  GitHub panel (git operations)
// ════════════════════════════════════════════════════════

const CommandsPanel = () => {
  const [output, setOutput] = useState([]);
  const [branches, setBranches] = useState([]);
  const [currentBranch, setCurrentBranch] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [fetched, setFetched] = useState(false);
  const [running, setRunning] = useState(null);

  const addOutput = (lines) => setOutput((prev) => [...prev, ...lines]);

  // ── Git handlers ──
  const handleStatus = async () => {
    setRunning('status');
    addOutput([{ type: 'cmd', text: 'git status' }]);
    try {
      const data = await api('/git/status/', { method: 'POST', body: '{}' });
      addOutput([{ type: data.success ? 'out' : 'error', text: data.stdout || data.stderr }]);
    } catch (e) {
      addOutput([{ type: 'error', text: e.error || 'Ошибка' }]);
    } finally { setRunning(null); }
  };

  const handleFetch = async () => {
    setRunning('fetch');
    addOutput([{ type: 'cmd', text: 'git fetch origin' }]);
    try {
      const data = await api('/git/fetch/', { method: 'POST', body: '{}' });
      const msg = data.stdout || data.stderr || 'Done';
      addOutput([{ type: data.success ? 'success' : 'error', text: msg }]);
      if (data.success) {
        addOutput([{ type: 'cmd', text: 'git branch -r' }]);
        const brData = await api('/git/branches/', { method: 'POST', body: '{}' });
        if (brData.success) {
          setBranches(brData.branches || []);
          setCurrentBranch(brData.current_branch || '');
          setFetched(true);
          addOutput([{ type: 'out', text: `Ветки: ${(brData.branches || []).join(', ')}\nТекущая: ${brData.current_branch}` }]);
        }
      }
    } catch (e) {
      addOutput([{ type: 'error', text: e.error || 'Ошибка' }]);
    } finally { setRunning(null); }
  };

  const handleCheckout = async () => {
    if (!selectedBranch) return;
    setRunning('checkout');
    addOutput([{ type: 'cmd', text: `git checkout ${selectedBranch.replace('origin/', '')}` }]);
    try {
      const data = await api('/git/checkout/', { method: 'POST', body: JSON.stringify({ branch: selectedBranch }) });
      addOutput([{ type: data.success ? 'success' : 'error', text: data.stdout || data.stderr }]);
      if (data.success) setCurrentBranch(selectedBranch.replace('origin/', ''));
    } catch (e) {
      addOutput([{ type: 'error', text: e.error || 'Ошибка' }]);
    } finally { setRunning(null); }
  };

  const handlePull = async () => {
    if (!selectedBranch) return;
    const branch = selectedBranch.replace('origin/', '');
    setRunning('pull');
    addOutput([{ type: 'cmd', text: `git pull origin ${branch}` }]);
    try {
      const data = await api('/git/pull/', { method: 'POST', body: JSON.stringify({ branch: selectedBranch }) });
      addOutput([{ type: data.success ? 'success' : 'error', text: data.stdout || data.stderr }]);
    } catch (e) {
      addOutput([{ type: 'error', text: e.error || 'Ошибка' }]);
    } finally { setRunning(null); }
  };

  // ── Frontend & Backend handlers ──
  const handleDeploy = async () => {
    if (!window.confirm('Скопировать React/ → src/ и запустить npm run build?')) return;
    setRunning('deploy');
    addOutput([{ type: 'cmd', text: 'rsync -av React/ → anatomage-app/src/ && npm run build' }]);
    try {
      const data = await api('/deploy-frontend/', { method: 'POST', body: '{}' });
      (data.steps || []).forEach((step) => {
        if (step.command) addOutput([{ type: 'cmd', text: step.command }]);
        if (step.stdout) addOutput([{ type: step.returncode === 0 ? 'out' : 'error', text: step.stdout }]);
        if (step.stderr) addOutput([{ type: 'error', text: step.stderr }]);
        if (step.returncode === 0) addOutput([{ type: 'success', text: `✔ ${step.step} завершён` }]);
        else addOutput([{ type: 'error', text: `✘ ${step.step} ошибка (код ${step.returncode})` }]);
      });
    } catch (e) {
      addOutput([{ type: 'error', text: e.error || 'Ошибка деплоя' }]);
    } finally { setRunning(null); }
  };

  const handleRestartBackend = async () => {
    if (!window.confirm('Перезапустить gunicorn + celery + redis?')) return;
    setRunning('restart');
    addOutput([{ type: 'cmd', text: 'systemctl restart gunicorn celery redis' }]);
    try {
      const data = await api('/restart/', { method: 'POST', body: JSON.stringify({ services: ['gunicorn', 'celery', 'redis'] }) });
      Object.entries(data.results || {}).forEach(([svc, res]) => {
        addOutput([{ type: res === 'ok' ? 'success' : 'error', text: `${svc}: ${res}` }]);
      });
      if (data.sudoers_hint) addOutput([{ type: 'error', text: data.sudoers_hint }]);
    } catch {
      addOutput([{ type: 'error', text: 'Ошибка перезапуска' }]);
    } finally { setRunning(null); }
  };

  const handleReloadBodies = async () => {
    setRunning('bodies');
    addOutput([{ type: 'cmd', text: 'python manage.py load_json_data' }]);
    try {
      const data = await api('/reload-bodies/', { method: 'POST', body: '{}' });
      if (data.stdout) addOutput([{ type: data.success ? 'out' : 'error', text: data.stdout }]);
      if (data.stderr) addOutput([{ type: 'error', text: data.stderr }]);
      if (data.success) addOutput([{ type: 'success', text: '✔ Тела перезагружены' }]);
      else addOutput([{ type: 'error', text: `✘ Ошибка (код ${data.returncode})` }]);
    } catch (e) {
      addOutput([{ type: 'error', text: e.error || 'Ошибка' }]);
    } finally { setRunning(null); }
  };

  return (
    <div>
      {currentBranch && (
        <div style={{ fontSize: '0.82rem', color: '#8aa8a2', marginBottom: 12 }}>Текущая ветка: <strong style={{ color: '#7ec8b8' }}>{currentBranch}</strong></div>
      )}

      <div className="admin-cmd-section-label">GitHub</div>
      <table className="admin-cmd-table">
        <tbody>
          <tr>
            <td><button className="admin-cmd-btn" onClick={handleStatus} disabled={!!running}>{running === 'status' ? '...' : 'git status'}</button></td>
            <td className="admin-cmd-desc">Статус репозитория</td>
          </tr>
          <tr>
            <td><button className="admin-cmd-btn" onClick={handleFetch} disabled={!!running}>{running === 'fetch' ? '...' : 'git fetch'}</button></td>
            <td className="admin-cmd-desc">Обновить список веток</td>
          </tr>
          <tr>
            <td>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <select className="admin-select" value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)} disabled={!fetched || !!running}>
                  <option value="">{fetched ? '— ветка —' : '— fetch —'}</option>
                  {branches.map((b) => <option key={b} value={b}>{b}{b.replace('origin/', '') === currentBranch ? ' ✓' : ''}</option>)}
                </select>
                <button className="admin-cmd-btn" onClick={handleCheckout} disabled={!selectedBranch || !!running}>{running === 'checkout' ? '...' : 'checkout'}</button>
              </div>
            </td>
            <td className="admin-cmd-desc">Переключить ветку</td>
          </tr>
          <tr>
            <td><button className="admin-cmd-btn" onClick={handlePull} disabled={!selectedBranch || !!running}>{running === 'pull' ? '...' : 'git pull'}</button></td>
            <td className="admin-cmd-desc">Подтянуть изменения</td>
          </tr>
        </tbody>
      </table>

      <div className="admin-cmd-section-label" style={{ marginTop: 18 }}>Frontend & Backend</div>
      <table className="admin-cmd-table">
        <tbody>
          <tr>
            <td><button className="admin-cmd-btn admin-cmd-btn--accent" onClick={handleDeploy} disabled={!!running}>{running === 'deploy' ? '...' : 'Собрать фронтенд'}</button></td>
            <td className="admin-cmd-desc">rsync + npm build</td>
          </tr>
          <tr>
            <td><button className="admin-cmd-btn admin-cmd-btn--accent" onClick={handleRestartBackend} disabled={!!running}>{running === 'restart' ? '...' : 'Перезапуск бэкенда'}</button></td>
            <td className="admin-cmd-desc">Перезапуск gunicorn + celery + redis</td>
          </tr>
          <tr>
            <td><button className="admin-cmd-btn" onClick={handleReloadBodies} disabled={!!running}>{running === 'bodies' ? '...' : 'Перезагрузить тела'}</button></td>
            <td className="admin-cmd-desc">python manage.py load_json_data</td>
          </tr>
        </tbody>
      </table>

      <TerminalOutput lines={output} onClear={() => setOutput([])} />
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  Logs content (extracted from LogsPanel for reuse)
// ════════════════════════════════════════════════════════

const LogsContent = () => {
  const [orchestrator, setOrchestrator] = useState([]);
  const [aiResponseFiles, setAiResponseFiles] = useState([]);
  const [aiResponses, setAiResponses] = useState([]);
  const [serviceLogs, setServiceLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/logs/');
      setOrchestrator(data.orchestrator || []);
      setAiResponseFiles(data.ai_response_files || []);
      setAiResponses(data.ai_responses || []);
      setServiceLogs(data.service_logs || []);
    } catch {
      setOrchestrator([]);
      setAiResponseFiles([]);
      setAiResponses([]);
      setServiceLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handleDownloadAll = async () => {
    setDownloading(true);
    try {
      const res = await apiBlob('/logs/download/');
      if (!res.ok) { setMsg('Ошибка скачивания'); setDownloading(false); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'ai_brain_logs.zip';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { setMsg('Ошибка скачивания'); }
    finally { setDownloading(false); }
  };

  const handleDownloadFile = async (relPath) => {
    try {
      const res = await apiBlob(`/logs/file/?path=${encodeURIComponent(relPath)}`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = relPath.split('/').pop();
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  const handleClear = async () => {
    if (!window.confirm('Очистить все лог-файлы?')) return;
    setClearing(true);
    try {
      const data = await api('/logs/clear/', { method: 'POST', body: '{}' });
      setMsg(`Очищено: ${data.cleared}`);
      loadLogs();
    } catch { setMsg('Ошибка очистки'); }
    finally { setClearing(false); }
  };

  const totalFiles = orchestrator.length + aiResponseFiles.length + serviceLogs.length + aiResponses.reduce((s, g) => s + g.files.length, 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="admin-btn admin-btn--primary" onClick={handleDownloadAll} disabled={downloading}>
          {downloading ? '...' : <><Icon src={downloadIcon} /> Скачать все (.zip)</>}
        </button>
        <button className="admin-btn admin-btn--danger" onClick={handleClear} disabled={clearing}>
          {clearing ? '...' : <><Icon src={trashIcon} /> Очистить</>}
        </button>
        <button className="admin-btn admin-btn--ghost" onClick={loadLogs}><Icon src={reloadIcon} /></button>
        <span style={{ fontSize: '0.78rem', color: '#8aa8a2', marginLeft: 'auto' }}>{totalFiles} файлов</span>
      </div>
      {msg && <div className="admin-success" style={{ marginBottom: 10 }}>{msg}</div>}
      {loading ? <div className="admin-spinner" /> : (
        <>
          <div className="admin-logs-section">
            <div className="admin-logs-section__title">🔧 Оркестратор</div>
            {orchestrator.length === 0 ? (
              <div className="admin-history__empty" style={{ padding: 12 }}>Нет лога</div>
            ) : orchestrator.map((l) => (
              <div key={l.name} className="admin-logs-file">
                <span className="admin-logs-file__name">{l.name}</span>
                <span className="admin-logs-file__size">{fmtSize(l.size)}</span>
                <span className="admin-logs-file__date">{new Date(l.modified * 1000).toLocaleString('ru-RU')}</span>
                <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => handleDownloadFile(l.name)}><Icon src={downloadIcon} /></button>
              </div>
            ))}
          </div>
          <div className="admin-logs-section" style={{ marginTop: 16 }}>
            <div className="admin-logs-section__title">🤖 AI логи ({aiResponseFiles.length} файлов)</div>
            {aiResponseFiles.length === 0 ? (
              <div className="admin-history__empty" style={{ padding: 12 }}>Нет логов</div>
            ) : aiResponseFiles.map((f) => (
              <div key={f.name} className="admin-logs-file">
                <span className="admin-logs-file__name">{f.name.split('/').pop()}</span>
                <span className="admin-logs-file__size">{fmtSize(f.size)}</span>
                <span className="admin-logs-file__date">{new Date(f.modified * 1000).toLocaleString('ru-RU')}</span>
                <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => handleDownloadFile(f.name)}><Icon src={downloadIcon} /></button>
              </div>
            ))}
          </div>
          <div className="admin-logs-section" style={{ marginTop: 16 }}>
            <div className="admin-logs-section__title">⚙️ Сервисы ({serviceLogs.length} файлов)</div>
            {serviceLogs.length === 0 ? (
              <div className="admin-history__empty" style={{ padding: 12 }}>Нет логов</div>
            ) : serviceLogs.map((f) => (
              <div key={f.name} className="admin-logs-file">
                <span className="admin-logs-file__name">{f.name.split('/').pop()}</span>
                <span className="admin-logs-file__size">{fmtSize(f.size)}</span>
                <span className="admin-logs-file__date">{new Date(f.modified * 1000).toLocaleString('ru-RU')}</span>
                <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => handleDownloadFile(f.name)}><Icon src={downloadIcon} /></button>
              </div>
            ))}
          </div>
          {aiResponses.length > 0 && (
            <div className="admin-logs-section" style={{ marginTop: 16 }}>
              <div className="admin-logs-section__title">📂 AI запросы ({aiResponses.length})</div>
              <div className="admin-logs-groups">
                {aiResponses.map((group, gi) => {
                  const gKey = `${group.assistant}/${group.request_id}`;
                  const isOpen = expandedGroup === gKey;
                  return (
                    <div key={gi} className="admin-logs-group">
                      <div className="admin-logs-group__header" onClick={() => setExpandedGroup(isOpen ? null : gKey)}>
                        <span className="admin-logs-group__arrow">{isOpen ? '▼' : '▶'}</span>
                        <span className="admin-logs-group__assistant">{group.assistant}</span>
                        <span className="admin-logs-group__id">{group.request_id}</span>
                        <span className="admin-logs-group__count">{group.files.length} файл(ов)</span>
                      </div>
                      {isOpen && (
                        <div className="admin-logs-group__files">
                          {group.files.map((f) => (
                            <div key={f.name} className="admin-logs-file">
                              <span className="admin-logs-file__name">{f.name.split('/').pop()}</span>
                              <span className="admin-logs-file__size">{fmtSize(f.size)}</span>
                              <span className="admin-logs-file__date">{new Date(f.modified * 1000).toLocaleString('ru-RU')}</span>
                              <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => handleDownloadFile(f.name)}><Icon src={downloadIcon} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  Roles panel (assistant prompt editor)
// ════════════════════════════════════════════════════════

const PIPELINE_TABS = [
  { key: 'generation', label: '🎬 Генерация' },
  { key: 'update', label: '✏️ Обновление' },
  { key: 'qa', label: '💬 Q&A' },
];

const RolesPanel = () => {
  const [pipelines, setPipelines] = useState({});
  const [activePipeline, setActivePipeline] = useState('generation');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { name, file, key, var }
  const [promptText, setPromptText] = useState('');
  const [promptLoading, setPromptLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [output, setOutput] = useState([]);

  const addOutput = (lines) => setOutput((prev) => [...prev, ...lines]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await api('/roles/');
        setPipelines(data.pipelines || {});
      } catch { setPipelines({}); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleEdit = async (assistant) => {
    const key = assistant.var || assistant.prompt_keys[0] || '';
    setEditing({ name: assistant.name, file: assistant.file, key, var: assistant.var || null });
    setPromptLoading(true);
    try {
      const data = await api('/roles/prompt/', {
        method: 'POST',
        body: JSON.stringify({ file: assistant.file, key, ...(assistant.var ? { var: assistant.var } : {}) }),
      });
      setPromptText(data.text || '');
    } catch { setPromptText(''); }
    finally { setPromptLoading(false); }
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    addOutput([{ type: 'cmd', text: `save → ${editing.file}` }]);
    try {
      const data = await api('/roles/save/', {
        method: 'POST',
        body: JSON.stringify({
          file: editing.file,
          key: editing.key,
          text: promptText,
          ...(editing.var ? { var: editing.var } : {}),
        }),
      });
      (data.steps || []).forEach((step) => {
        if (step.command) addOutput([{ type: 'cmd', text: step.command }]);
        if (step.stdout) addOutput([{ type: step.returncode === 0 ? 'out' : 'error', text: step.stdout }]);
        if (step.stderr) addOutput([{ type: 'error', text: step.stderr }]);
        if (step.returncode === 0) addOutput([{ type: 'success', text: `✔ ${step.step}` }]);
        else addOutput([{ type: 'error', text: `✘ ${step.step} (код ${step.returncode})` }]);
      });
    } catch (e) {
      addOutput([{ type: 'error', text: e.error || 'Ошибка сохранения' }]);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-spinner" />;

  // ── Editor view ──
  if (editing) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <button className="admin-cmd-btn" onClick={() => setEditing(null)}>← Назад</button>
          <span style={{ fontSize: '0.92rem', fontWeight: 600, color: '#c8e6e0' }}>{editing.name}</span>
          <span style={{ fontSize: '0.75rem', color: '#6a9a8e', marginLeft: 'auto' }}>{editing.file}</span>
        </div>
        {promptLoading ? <div className="admin-spinner" /> : (
          <textarea
            className="admin-roles-editor"
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            spellCheck={false}
          />
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <button className="admin-cmd-btn admin-cmd-btn--accent" onClick={handleSave} disabled={saving || promptLoading}>
            {saving ? '...' : 'Сохранить и запушить'}
          </button>
          <button className="admin-cmd-btn" onClick={() => setEditing(null)} disabled={saving}>Отмена</button>
        </div>
        <TerminalOutput lines={output} onClear={() => setOutput([])} />
      </div>
    );
  }

  const currentPipeline = pipelines[activePipeline];
  const phases = currentPipeline?.phases || [];

  // ── Pipeline selector + phase list view ──
  return (
    <div>
      <div className="pd-body-toggle" style={{ marginBottom: 16 }}>
        {PIPELINE_TABS.map((tab) => (
          <button
            key={tab.key}
            className={`pd-body-toggle__btn${activePipeline === tab.key ? ' pd-body-toggle__btn--active' : ' pd-body-toggle__btn--inactive'}`}
            onClick={() => setActivePipeline(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {currentPipeline && (
        <div style={{ fontSize: '0.78rem', color: '#8aa8a2', marginBottom: 14 }}>
          {currentPipeline.description}
        </div>
      )}
      {phases.map((phase) => (
        <div key={phase.phase} className="admin-roles-phase">
          <div className="admin-roles-phase__header">
            <span className="admin-roles-phase__num">Фаза {phase.phase}</span>
            <span className="admin-roles-phase__name">{phase.name}</span>
          </div>
          <div className="admin-roles-phase__desc">{phase.description}</div>
          <table className="admin-cmd-table">
            <tbody>
              {phase.assistants.map((a) => (
                <tr key={a.file}>
                  <td>
                    <button className="admin-cmd-btn" onClick={() => handleEdit(a)} disabled={a.prompt_keys.length === 0 && !a.var}>
                      {a.name}
                    </button>
                  </td>
                  <td className="admin-cmd-desc">
                    {a.var ? a.var : a.prompt_keys.length > 0 ? a.prompt_keys.join(', ') : 'нет промпта'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  Server panel (modal with Logs / GitHub / Restart tabs)
// ════════════════════════════════════════════════════════

const SERVER_TABS = [
  { key: 'logs', label: 'Логи' },
  { key: 'commands', label: 'Команды' },
  { key: 'roles', label: 'Роли' },
];

const ServerPanel = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('logs');

  return (
    <div className="admin-overlay" onClick={onClose}>
      <div className="admin-modal admin-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <span className="admin-modal__title"><Icon src={terminalIcon} /> Сервер</span>
          <button className="admin-modal__close" onClick={onClose}>✕</button>
        </div>
        <div className="admin-server-tabs">
          <div className="pd-body-toggle">
            {SERVER_TABS.map((tab) => (
              <button
                key={tab.key}
                className={`pd-body-toggle__btn${activeTab === tab.key ? ' pd-body-toggle__btn--active' : ' pd-body-toggle__btn--inactive'}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        <div className="admin-modal__body">
          {activeTab === 'logs' && <LogsContent />}
          {activeTab === 'commands' && <CommandsPanel />}
          {activeTab === 'roles' && <RolesPanel />}
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  Main dashboard
// ════════════════════════════════════════════════════════

const AdminDashboard = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showServer, setShowServer] = useState(false);
  const [renewToken, setRenewToken] = useState(null);
  const [editToken, setEditToken] = useState(null);
  const [chatToken, setChatToken] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchChatText, setSearchChatText] = useState('');
  const [searchChatResults, setSearchChatResults] = useState([]);
  const [searchChatLoading, setSearchChatLoading] = useState(false);
  const [searchJsonText, setSearchJsonText] = useState('');
  const [searchJsonResults, setSearchJsonResults] = useState([]);
  const [searchJsonLoading, setSearchJsonLoading] = useState(false);
  const [searchPresetName, setSearchPresetName] = useState('');
  const [searchPresetResults, setSearchPresetResults] = useState([]);
  const [searchPresetLoading, setSearchPresetLoading] = useState(false);

  const loadTokens = useCallback(async () => {
    try {
      const data = await api('/tokens/');
      setTokens(data.tokens || []);
      setError(null);
    } catch (err) {
      if (err.status === 401) {
        sessionStorage.removeItem('admin_password');
        window.location.reload();
        return;
      }
      setError(err.error || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTokens(); }, [loadTokens]);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_password');
    window.location.reload();
  };

  const handleToggle = async (token) => {
    const action = token.active ? 'deactivate' : 'activate';
    setActionLoading(token.token_hash);
    try {
      await api(`/tokens/${action}/`, { method: 'POST', body: JSON.stringify({ token_hash: token.token_hash }) });
      loadTokens();
    } catch { }
    finally { setActionLoading(null); }
  };

  const totalRequests = tokens.reduce((s, t) => s + t.current_month_requests, 0);
  const activeCount = tokens.filter((t) => t.active).length;

  // ── Search handlers ──
  const handleSearchChatText = async (e) => {
    e.preventDefault();
    if (!searchChatText.trim()) return;
    setSearchChatLoading(true);
    try {
      const data = await api(`/search/chat-text/?q=${encodeURIComponent(searchChatText)}`);
      setSearchChatResults(data.results || []);
    } catch { setSearchChatResults([]); }
    finally { setSearchChatLoading(false); }
  };

  const handleSearchJsonText = async (e) => {
    e.preventDefault();
    if (!searchJsonText.trim()) return;
    setSearchJsonLoading(true);
    try {
      const data = await api(`/search/json-text/?q=${encodeURIComponent(searchJsonText)}`);
      setSearchJsonResults(data.results || []);
    } catch { setSearchJsonResults([]); }
    finally { setSearchJsonLoading(false); }
  };

  const handleSearchPresetName = async (e) => {
    e.preventDefault();
    if (!searchPresetName.trim()) return;
    setSearchPresetLoading(true);
    try {
      const data = await api(`/search/preset-name/?q=${encodeURIComponent(searchPresetName)}`);
      setSearchPresetResults(data.results || []);
    } catch { setSearchPresetResults([]); }
    finally { setSearchPresetLoading(false); }
  };

  return (
    <div className="admin-root">
      <div className="admin-container">
        {/* Header */}
        <div className="admin-header">
          <h1 className="admin-header__title">🔑 Управление API</h1>
          <div className="admin-header__actions">
            <button className="pd-header-btn" onClick={() => setShowCreate(true)}><Icon src={plusIcon} /> Токен</button>
            <button className="pd-header-btn" onClick={() => setShowServer(true)}><Icon src={terminalIcon} /> Сервер</button>
            <button className={`pd-header-btn${showSearch ? ' pd-header-btn--active' : ''}`} onClick={() => setShowSearch((v) => !v)}><Icon src={historyIcon} /> Поиск</button>
            <button className="pd-header-btn" onClick={handleLogout}><Icon src={exitIcon} /> Выход</button>
          </div>
        </div>

        {error && <div className="admin-error">{error}</div>}
        {success && <div className="admin-success">{success}</div>}

        {/* Stats table */}
        <div className="admin-stats-table">
          <table className="admin-history__table">
            <thead>
              <tr>
                <th>Всего токенов</th>
                <th>Активных</th>
                <th>Отключённых</th>
                <th>Запросов / мес</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ fontWeight: 700, fontSize: '1rem' }}>{tokens.length}</td>
                <td style={{ fontWeight: 700, fontSize: '1rem', color: '#66bb6a' }}>{activeCount}</td>
                <td style={{ fontWeight: 700, fontSize: '1rem', color: '#ef5350' }}>{tokens.length - activeCount}</td>
                <td style={{ fontWeight: 700, fontSize: '1rem' }}>{totalRequests}</td>
              </tr>
            </tbody>
          </table>
        </div>


        {/* Tokens table */}
        {loading ? <div className="admin-spinner" /> : tokens.length === 0 ? (
          <div className="admin-empty">
            <div className="admin-empty__icon">🔑</div>
            <div className="admin-empty__text">Пока нет API токенов</div>
          </div>
        ) : (
          <div className="admin-tokens-table-wrap">
            <table className="admin-tokens-table">
              <thead>
                <tr>
                  <th>Имя</th>
                  <th>Статус</th>
                  <th>Запросов</th>
                  <th>Дней осталось</th>
                  <th>Создан</th>
                  <th>Истекает</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => {
                  const days = daysLeft(t.expires_at);
                  const usagePercent = t.max_requests_per_month > 0
                    ? (t.current_month_requests / t.max_requests_per_month) * 100
                    : 0;
                  const usageClass = usagePercent >= 90 ? 'danger' : usagePercent >= 70 ? 'warning' : 'good';
                  const daysClass = days !== null && days <= 7 ? 'danger' : days !== null && days <= 30 ? 'warning' : 'good';

                  return (
                    <tr key={t.token_hash} className={`admin-tokens-table__row${!t.active ? ' admin-tokens-table__row--inactive' : ''}`}>
                        <td>
                          <div className="admin-tokens-table__name">{t.name}</div>
                          <div className="admin-tokens-table__hash">{t.token_hash.slice(0, 16)}…</div>
                        </td>
                        <td>
                          <span className={`admin-card__badge admin-card__badge--${t.active ? 'active' : 'inactive'}`}>
                            {t.active ? 'Активен' : 'Откл'}
                          </span>
                        </td>
                        <td>
                          <span className={`admin-stat__value--${usageClass}`} style={{ fontWeight: 700 }}>
                            {t.current_month_requests}
                          </span>
                          <span style={{ color: '#6b8a84' }}> / {t.max_requests_per_month}</span>
                          <div style={{ fontSize: '0.68rem', color: '#5a7a74', marginTop: 2 }}>
                            {t.reset_requests === false ? 'общий' : 'за 30 дн.'}
                          </div>
                          <div className="admin-usage-bar" style={{ marginTop: 4 }}>
                            <div className={`admin-usage-bar__fill admin-usage-bar__fill--${usageClass}`} style={{ width: `${Math.min(usagePercent, 100)}%` }} />
                          </div>
                        </td>
                        <td>
                          <span className={`admin-stat__value--${daysClass}`} style={{ fontWeight: 700 }}>
                            {days !== null ? days : '—'}
                          </span>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(t.created_at)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(t.expires_at)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <button className="admin-btn admin-btn--sm admin-btn--primary" onClick={() => setChatToken(t)} title="Переписки"><Icon src={chatIcon} /></button>
                            <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => setEditToken(t)} title="Редактировать"><Icon src={editIcon} /></button>
                            <button className="admin-btn admin-btn--sm admin-btn--ghost" onClick={() => setRenewToken(t)} title="Продлить"><Icon src={timeIcon} /></button>
                            <button
                              className={`admin-btn admin-btn--sm ${t.active ? 'admin-btn--danger' : 'admin-btn--success'}`}
                              disabled={actionLoading === t.token_hash}
                              onClick={() => handleToggle(t)}
                              title={t.active ? 'Деактивировать' : 'Активировать'}
                            >
                              {actionLoading === t.token_hash ? '...' : t.active ? <Icon src={pauseIcon} /> : <Icon src={playIcon} />}
                            </button>
                          </div>
                        </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Modals */}
        {showCreate && (
          <CreateTokenModal
            onClose={() => setShowCreate(false)}
            onCreated={() => { loadTokens(); setSuccess('Токен создан'); setTimeout(() => setSuccess(null), 4000); }}
          />
        )}
        {renewToken && (
          <RenewModal
            tokenHash={renewToken.token_hash}
            tokenName={renewToken.name}
            onClose={() => setRenewToken(null)}
            onDone={loadTokens}
          />
        )}
        {editToken && (
          <EditTokenModal
            token={editToken}
            onClose={() => setEditToken(null)}
            onDone={loadTokens}
          />
        )}
        {chatToken && (
          <ChatListModal
            tokenHash={chatToken.token_hash}
            tokenName={chatToken.name}
            onClose={() => setChatToken(null)}
          />
        )}
        {showServer && <ServerPanel onClose={() => setShowServer(false)} />}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════
//  Root: auth gate
// ════════════════════════════════════════════════════════

const AdminTokenManager = () => {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const pw = sessionStorage.getItem('admin_password');
    if (!pw) { setChecking(false); return; }
    api('/verify-password/', { method: 'POST', body: '{}' })
      .then(() => setAuthed(true))
      .catch(() => sessionStorage.removeItem('admin_password'))
      .finally(() => setChecking(false));
  }, []);

  if (checking) return <div className="admin-root"><div className="admin-spinner" /></div>;
  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;
  return <AdminDashboard />;
};

export default AdminTokenManager;
